import React, {CSSProperties, FC, ReactNode, useEffect, useRef} from 'react'
import {makeStyles} from '@material-ui/core'
import clsx from 'clsx'
import {Layout} from 'react-grid-layout'
import ControlCameraIcon from '@material-ui/icons/ControlCamera'
import {SSMPalette} from '../AppTheme'
import {RGL_WIDGET_RESIZE} from '../../AppConstants'

export type WidgetSize = {
    width: number,
    height: number
}
export type WidgetPosition = {
    w: number,
    h: number,
    x: number,
    y: number
}
export type WidgetItem = {
    gridItem: Layout,
    label?: string,
    bodySpacing?: number,
    node: (size: WidgetSize) => ReactNode,
    customStyles?: CSSProperties
    headerClassName?: string;
    isPrimaryHeaderStyle?: boolean;
}
type WidgetProps = {
    children: (size: WidgetSize) => ReactNode,
    label?: string,
    bodySpacing?: number,
    headerClassName?: string;
    isPrimaryHeaderStyle?: boolean;
}

const useStyles = makeStyles(theme => ({
    container: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: 0,
        top: 0,
        display: 'flex',
        fontFamily: '"Segoe UI", "Helvetica Neue", "Trebuchet MS", Verdana, sans-serif',
        color: SSMPalette.common.chart,
        background: SSMPalette.common.white,
        flexDirection: 'column',
        borderRadius: theme.spacing(.5),
        boxShadow: 'rgb(0 0 0 / 30%) 0 0px 3px',
        '& ~ .react-resizable-handle': {
            background: 'none',
            zIndex: 10,
            '&::after': {
                width: theme.spacing(1),
                height: theme.spacing(1),
                borderRadius: theme.spacing(0, 0, .5, 0),
                borderBottom: `solid 1px ${SSMPalette.gray.silver}`,
                borderRight: `solid 1px ${SSMPalette.gray.silver}`,
            },
        },
        '.react-grid-item.static &': {
            boxShadow: 'none',
        },
    },
    header: {
        cursor: 'move',
        display: 'flex',
        flex: 0,
        alignItems: 'center',
        padding: theme.spacing(0, 1),
        borderBottom: `solid 1px ${SSMPalette.gray.sand}`,
        height: theme.spacing(3.5),
        '.react-grid-item.static &': {
            display: 'none',
        },
    },
    label: {
        flex: 1,
        fontSize: 12,
        marginRight: theme.spacing(1),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    move: {
        height: theme.spacing(3.5),
        fontSize: 16,
        color: SSMPalette.gray.fossil,
        marginLeft: 'auto',
    },
    body: {
        flex: 1,
        height: 0,
        margin: (props: WidgetProps) => theme.spacing(props.bodySpacing !== undefined ? props.bodySpacing : 1),
        position: 'relative',
        '.react-grid-item.static &': {
            padding: 0,
        },
    },
    primaryHeader: {
        backgroundColor: SSMPalette.primary.light,
        color: SSMPalette.gray.sand,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,

        '& svg': {
            color: 'inherit',
        },
    },
}))
export const Widget: FC<WidgetProps> = (props) => {
    const classes = useStyles(props)
    const bodyRef = useRef<HTMLDivElement>(null)
    const [widgetSize, setWidgetSize] = React.useState<WidgetSize>({width: 0, height: 0})

    const updateContainerSizes = () => {
        if (bodyRef.current) {
            setWidgetSize({width: bodyRef.current.offsetWidth, height: bodyRef.current.offsetHeight})
        }
    }
    useEffect(() => {
        document.addEventListener(RGL_WIDGET_RESIZE, updateContainerSizes)

        return () => {
            document.removeEventListener(RGL_WIDGET_RESIZE, updateContainerSizes)
        }
    }, [])

    return (
        <div className={classes.container}>
            <div
                className={clsx('js-draggableHandle', classes.header, props.isPrimaryHeaderStyle && classes.primaryHeader, props.headerClassName)}>
                {props.label && <div className={classes.label}>{props.label}</div>}
                <ControlCameraIcon className={classes.move}/>
            </div>
            <div className={`js-draggableCancel ${classes.body}`} ref={bodyRef}>
                {props.children(widgetSize)}
            </div>
        </div>
    )
}
